import React from 'react';
import { useAuth } from '../../context/AuthContext';
import './GuideCard.css';

// Placeholders for guides without thumbnail
const DEFAULT_THUMBNAIL = '/default-guide-thumbnail.svg';
const DRAFT_PLACEHOLDER = '/draft-placeholder.svg';

// Remove outer <p> wrappers (common from ReactQuill) and ignore empty <p><br></p>
const stripOuterPTags = (html = '') =>
  (html || '')
    .replace(/^<p>/i, '')
    .replace(/<\/p>$/i, '')
    .replace(/<p><br><\/p>/gi, '');

const getFirstPContent = (html = '') => {
  if (!html) return '';

  const match = html.match(/<p[^>]*>(.*?)<\/p>/i);
  const content = match ? match[1] : html;

  // Remove formatting tags but keep their inner text
  return content.replace(/<\/?(strong|b|em|i|u)[^>]*>/gi, '');
};

const GuideCard = ({
  guide,
  isDraft,
  isSelected,
  onSelect,
}) => {
  const { user } = useAuth();

  // Use draft placeholder for drafts without thumbnail, default thumbnail for published
  const getPlaceholder = () => isDraft ? DRAFT_PLACEHOLDER : DEFAULT_THUMBNAIL;
  const thumbnailSrc = guide.thumbnail_url || getPlaceholder();

  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect();
    }
  };

  return (
    <div
      className={`guide-card ${isSelected ? 'selected' : ''}`}
      onClick={handleClick}
    >
      {/* Selection Checkmark */}
      {isSelected && (
        <div className="guide-card-checkmark">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="11" fill="#AE2185" stroke="#AE2185" strokeWidth="2"/>
            <path d="M7 12L10.5 15.5L17 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}

      {/* Preview Thumbnail */}
      <div className="guide-card-preview">
        <img
          src={thumbnailSrc}
          alt={guide.title || 'Quick Guide'}
          className="preview-thumbnail-image"
          onError={(e) => {
            // Fallback to appropriate placeholder if image fails to load
            e.target.src = getPlaceholder();
          }}
        />
      </div>

      {/* Card Footer */}
      <div className="guide-card-footer">
        <div className="guide-card-info">
          <div className="guide-card-title-row">
            <h3
              className="guide-card-title"
              dangerouslySetInnerHTML={{
                __html: getFirstPContent(guide.title) || 'Quick Guide Title',
              }}
            />
            {!isDraft && (
              <div className="guide-card-lock">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_18227_4245)">
                  <path d="M5 13C5 12.4696 5.21071 11.9609 5.58579 11.5858C5.96086 11.2107 6.46957 11 7 11H17C17.5304 11 18.0391 11.2107 18.4142 11.5858C18.7893 11.9609 19 12.4696 19 13V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21H7C6.46957 21 5.96086 20.7893 5.58579 20.4142C5.21071 20.0391 5 19.5304 5 19V13Z" stroke="#3F3F3F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M11 16C11 16.2652 11.1054 16.5196 11.2929 16.7071C11.4804 16.8946 11.7348 17 12 17C12.2652 17 12.5196 16.8946 12.7071 16.7071C12.8946 16.5196 13 16.2652 13 16C13 15.7348 12.8946 15.4804 12.7071 15.2929C12.5196 15.1054 12.2652 15 12 15C11.7348 15 11.4804 15.1054 11.2929 15.2929C11.1054 15.4804 11 15.7348 11 16Z" stroke="#3F3F3F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 11V7C8 5.93913 8.42143 4.92172 9.17157 4.17157C9.92172 3.42143 10.9391 3 12 3C13.0609 3 14.0783 3.42143 14.8284 4.17157C15.5786 4.92172 16 5.93913 16 7V11" stroke="#3F3F3F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_18227_4245">
                  <rect width="24" height="24" fill="white"/>
                  </clipPath>
                  </defs>
                  </svg>
              </div>
            )}
          </div>
          {!isDraft && (
            <div className="guide-card-author">
              {/* <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M9.5 10.5V9.5C9.5 8.96957 9.28929 8.46086 8.91421 8.08579C8.53914 7.71071 8.03043 7.5 7.5 7.5H4.5C3.96957 7.5 3.46086 7.71071 3.08579 8.08579C2.71071 8.46086 2.5 8.96957 2.5 9.5V10.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 5.5C7.10457 5.5 8 4.60457 8 3.5C8 2.39543 7.10457 1.5 6 1.5C4.89543 1.5 4 2.39543 4 3.5C4 4.60457 4.89543 5.5 6 5.5Z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
              </svg> */}
              Created by {guide.user_id === user?.id ? 'You' : ((guide.created_by_name && guide.created_by_name !== 'Unknown' && guide.created_by_name !== 'Unknown Author') ? guide.created_by_name : (user?.name || user?.email?.split('@')[0] || 'You'))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuideCard;
